import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function makeSessionId() {
  return `lsl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

const GREETING = "Hi, I'm Grace 👋 — your Legacy Security Life assistant. Ask me anything about final expense insurance, coverage amounts, eligibility, or how the process works.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(makeSessionId);
  const [messages, setMessages] = useState([
    { role: "assistant", content: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm sorry — I had trouble connecting. Please try again, or call us at 833-309-LIFE." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-widget-toggle"
        aria-label="Toggle chat assistant"
        className={`fixed z-[60] bottom-24 right-5 sm:right-6 inline-flex items-center justify-center h-14 w-14 rounded-full shadow-2xl transition-all ${
          open ? "bg-white text-navy border border-[#C5A059]" : "bg-[#0B203B] text-[#E8C97A] hover:scale-105"
        }`}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed z-[60] bottom-44 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[68vh] bg-white rounded-xl shadow-2xl border border-[#C5A059]/40 flex flex-col overflow-hidden fade-up"
        >
          <div className="bg-[#0B203B] text-white px-5 py-4 flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#C5A059] text-[#0B203B] font-serif text-lg">G</span>
            <div className="flex-1">
              <p className="font-serif text-base leading-tight">Grace · Live Assistant</p>
              <p className="text-[11px] tracking-[0.16em] uppercase text-[#E8C97A] flex items-center gap-1.5 mt-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 soft-pulse" /> Online now
              </p>
            </div>
          </div>

          <div ref={scrollRef} className="chat-scroll flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`max-w-[85%] px-4 py-2.5 text-[15px] leading-relaxed ${
                  m.role === "user" ? "chat-bubble-user ml-auto" : "chat-bubble-bot"
                }`}
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="chat-bubble-bot max-w-[85%] px-4 py-2.5 text-[15px] italic text-navy/60" data-testid="chat-typing">
                Grace is typing…
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-border bg-white p-3 flex items-center gap-2">
            <Input
              data-testid="chat-input"
              placeholder="Ask about coverage, eligibility, premiums…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="h-11 border-[#cfd6e0]"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="chat-send-btn"
              className="inline-flex items-center justify-center h-11 w-11 rounded-md bg-[#C5A059] hover:bg-[#B08A3E] disabled:opacity-50 text-[#0B203B]"
              aria-label="Send"
            >
              <Send className="h-5 w-5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
